import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import * as services from "../../../services/saleng";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import { formatNumber } from "../../../utils/formatNumber";

export default function TopMonthlyPurchasesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const STATUS = "payed";

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const fetchRows = async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectedYear, selectedMonth, 1)
        .toISOString()
        .split("T")[0];
      const endDate = new Date(selectedYear, selectedMonth + 1, 0)
        .toISOString()
        .split("T")[0];

      const res = await services.getPurchaseTransactions({
        status: STATUS,
        startDate,
        endDate,
      });

      const data = res?.data?.data ?? [];
      console.log("data", data);

      const grouped = new Map();

      for (const t of data) {
        const cid = t.customerId ?? "unknown";
        const key = String(cid);

        const sumNet =
          t.items?.reduce((s, it) => s + (it.netPrice || 0), 0) || 0;

        const vatIncluded = t.isVat === true;
        const beforeVat = vatIncluded ? sumNet / 1.07 : sumNet;
        const vat = vatIncluded ? sumNet - beforeVat : 0;

        if (!grouped.has(key)) {
          grouped.set(key, {
            customerId: cid,
            customerCode: t.customer?.code || "",
            customerName: t.customer?.name || t.customerName || "-",
            branchName: t.branch?.name || "-",
            personType: t.customer?.personType || "-",
            weight: 0,
            totalBeforeVat: 0,
            vatAmount: 0,
            grandTotal: 0,
          });
        }

        const g = grouped.get(key);
        g.weight += t.items?.reduce((s, it) => s + (it.netWeight || 0), 0) || 0;
        g.totalBeforeVat += beforeVat;
        g.vatAmount += vat;
        g.grandTotal += sumNet;
      }

      const result = Array.from(grouped.values())
        .sort((a, b) => b.grandTotal - a.grandTotal)
        .slice(0, 10);

      setRows(result);
    } catch (err) {
      console.error("โหลดข้อมูลไม่สำเร็จ", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [selectedMonth, selectedYear]);

  const cellStyle = { border: "1px solid #ddd", whiteSpace: "nowrap" };

  const months = [
    "มกราคม",
    "กุมภาพันธ์",
    "มีนาคม",
    "เมษายน",
    "พฤษภาคม",
    "มิถุนายน",
    "กรกฎาคม",
    "สิงหาคม",
    "กันยายน",
    "ตุลาคม",
    "พฤศจิกายน",
    "ธันวาคม",
  ];

  const mapPersonType = {
    normal: "บุคคลธรรมดา",
    company: "นิติบุคคล",
  };

  return (
    <Box sx={{ p: 2 }}>
      <AppBreadcrumbs />
      <Box sx={{ display: "flex", gap: 2, my: 2, alignItems: "center" }}>
        <Typography variant="h6">
          10 อันดับ ยอดซื้อรายบุคคลประจำเดือน
        </Typography>
        <FormControl size="small">
          <InputLabel>เดือน</InputLabel>
          <Select
            value={selectedMonth}
            label="เดือน"
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {months.map((m, i) => (
              <MenuItem key={i} value={i}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>ปี</InputLabel>
          <Select
            value={selectedYear}
            label="ปี"
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {[selectedYear - 1, selectedYear, selectedYear + 1].map((y) => (
              <MenuItem key={y} value={y}>
                {y + 543} {/* แสดงปี พ.ศ. */}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={cellStyle} align="center">
                  อันดับ
                </TableCell>
                <TableCell sx={cellStyle}>ซัพพลายเออร์</TableCell>
                <TableCell sx={cellStyle}>สาขา</TableCell>
                <TableCell sx={cellStyle}>ประเภทซัพพลายเออร์</TableCell>
                <TableCell sx={cellStyle} align="right">
                  น้ำหนัก
                </TableCell>
                <TableCell sx={cellStyle}>หน่วย</TableCell>
                <TableCell sx={cellStyle} align="right">
                  ยอดก่อนรวมภาษี
                </TableCell>
                <TableCell sx={cellStyle} align="right">
                  ยอดภาษี
                </TableCell>
                <TableCell sx={cellStyle} align="right">
                  ยอดสุทธิ
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={row.customerId} hover>
                  <TableCell sx={cellStyle} align="center">
                    {idx + 1}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {row.customerCode} {row.customerName}
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.branchName}</TableCell>
                  <TableCell sx={cellStyle}>
                    {mapPersonType[row.personType]}
                  </TableCell>
                  <TableCell sx={cellStyle} align="right">
                    {formatNumber(row.weight)}
                  </TableCell>
                  <TableCell sx={cellStyle}>-</TableCell>
                  <TableCell sx={cellStyle} align="right">
                    {formatNumber(row.totalBeforeVat)}
                  </TableCell>
                  <TableCell sx={cellStyle} align="right">
                    {formatNumber(row.vatAmount)}
                  </TableCell>
                  <TableCell sx={cellStyle} align="right">
                    {formatNumber(row.grandTotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
